import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  CreditCard,
  History,
  MessageSquare,
  MoreHorizontal,
  Plus,
  Search,
  Trash2,
  User,
} from "lucide-react";
import { getNativeClientPlatformPlans, deleteNativeClientPlatformPlan } from "../../../services/native-client-platform-plan";
import { toast } from "sonner";
import useDebounce from "@/hooks/useDebounce";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { ConfirmDialog } from "../../../components/ui/dialog";
import { DropdownMenu } from "../../../components/ui/dropdown-menu";
import { EmptyState } from "../../../components/ui/empty-state";
import { Pagination } from "../../../components/ui/pagination";
import { cn } from "../../../lib/cn";

export default function NativeClientPlatformPlansList() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const navigate = useNavigate();

  const fetchPlans = useCallback(() => {
    setLoading(true);
    getNativeClientPlatformPlans({ search: debouncedSearch, searchField: "name", page, perPage })
      .then((response) => {
        const plans = Array.isArray(response) ? response : (response.data || []);
        const count = response.total ?? plans.length;
        setData(plans);
        setTotal(count);
      })
      .catch(() => toast.error("Falha ao carregar planos"))
      .finally(() => setLoading(false));
  }, [debouncedSearch, page, perPage]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const formatCurrency = (v) =>
    v?.toLocaleString("pt-br", { style: "currency", currency: "BRL", minimumFractionDigits: 2 });

  const fmtNum = (v) => (v == null ? "∞" : v.toLocaleString("pt-BR"));

  return (
    <div className="dash-scroll flex h-full w-full flex-col overflow-y-auto bg-[var(--dash-bg)]">
      <div className="mx-auto w-full max-w-[1400px] px-6 py-8">
        <header className="mb-6 flex flex-col gap-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-2xl font-semibold tracking-tight text-[var(--dash-text)]">
                Planos da Plataforma
              </h1>
              <p className="text-xs text-[var(--dash-text-muted)]">
                {total} plano{total !== 1 ? "s" : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-60">
                <Input
                  leftIcon={Search}
                  placeholder="Buscar..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <Button variant="primary" leftIcon={Plus} onClick={() => navigate("create")}>
                Novo plano
              </Button>
            </div>
          </div>
        </header>

        {loading && data.length === 0 ? (
          <PlanSkeleton />
        ) : data.length === 0 ? (
          <div className="rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
            <EmptyState
              icon={CreditCard}
              title={debouncedSearch ? "Nenhum resultado" : "Crie seu primeiro plano"}
              description={
                debouncedSearch
                  ? `Nada encontrado para "${debouncedSearch}".`
                  : "Planos definem limites e features para plataformas de clientes."
              }
              action={
                !debouncedSearch && (
                  <Button variant="primary" size="lg" leftIcon={Plus} onClick={() => navigate("create")}>
                    Criar primeiro plano
                  </Button>
                )
              }
            />
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
            <div className="hidden items-center gap-4 border-b border-[var(--dash-border)] px-5 py-3 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--dash-text-muted)] md:flex">
              <div className="flex-1">Nome</div>
              <div className="w-28 text-right">Preço</div>
              <div className="w-52">Limites</div>
              <div className="w-40">Features</div>
              <div className="w-8" />
            </div>

            <div className="divide-y divide-[var(--dash-border)]">
              {data.map((item, i) => (
                <PlanRow
                  key={item.id}
                  item={item}
                  index={i}
                  formatCurrency={formatCurrency}
                  fmtNum={fmtNum}
                  onDeleted={fetchPlans}
                />
              ))}
            </div>
          </div>
        )}

        {total > perPage && (
          <Pagination page={page} pageSize={perPage} total={total} onPageChange={setPage} />
        )}
      </div>
    </div>
  );
}

function PlanRow({ item, index, formatCurrency, fmtNum, onDeleted }) {
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const onDelete = useCallback(() => {
    setDeleting(true);
    deleteNativeClientPlatformPlan(item.id)
      .then(() => { toast.success("Plano removido!"); setConfirmDelete(false); onDeleted(); })
      .catch(() => toast.error("Falha ao remover"))
      .finally(() => setDeleting(false));
  }, [item.id, onDeleted]);

  const menuItems = useMemo(() => [
    { label: "Editar", icon: ChevronRight, onClick: () => navigate(`${item.id}`) },
    { separator: true },
    { label: "Remover", icon: Trash2, danger: true, onClick: () => setConfirmDelete(true) },
  ], [item.id, navigate]);

  const features = item.features || [];
  const visibleFeatures = features.slice(0, 2);
  const remaining = features.length - 2;

  return (
    <>
      <div
        className="group flex cursor-pointer items-center gap-4 px-5 py-3.5 transition-colors hover:bg-[var(--dash-surface-hover)]"
        onClick={() => navigate(`${item.id}`)}
        style={{ animationDelay: `${index * 30}ms` }}
      >
        {/* Name */}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-sm font-medium text-[var(--dash-text)]">{item.name}</span>
          {/* Mobile-only info */}
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-[var(--dash-text-muted)] md:hidden">
            <span className="font-semibold text-emerald-400">{formatCurrency(item.price)}/mês</span>
            <span className="flex items-center gap-1"><MessageSquare size={10} /> {fmtNum(item.maxMessages)}</span>
            <span className="flex items-center gap-1"><User size={10} /> {fmtNum(item.maxUsers)}</span>
          </div>
        </div>

        {/* Price */}
        <div className="hidden w-28 text-right md:block">
          <span className="text-sm font-semibold tabular-nums text-emerald-400">{formatCurrency(item.price)}</span>
          <span className="ml-0.5 text-[10px] text-[var(--dash-text-faint)]">/mês</span>
        </div>

        {/* Limits */}
        <div className="hidden w-52 md:flex">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-[11px] text-[var(--dash-text-muted)]" title="Mensagens">
              <MessageSquare size={12} className="text-[var(--dash-text-faint)]" />
              <span className="tabular-nums">{fmtNum(item.maxMessages)}</span>
            </span>
            <span className="flex items-center gap-1.5 text-[11px] text-[var(--dash-text-muted)]" title="Usuários">
              <User size={12} className="text-[var(--dash-text-faint)]" />
              <span className="tabular-nums">{fmtNum(item.maxUsers)}</span>
            </span>
            <span className="flex items-center gap-1.5 text-[11px] text-[var(--dash-text-muted)]" title="Sessões">
              <History size={12} className="text-[var(--dash-text-faint)]" />
              <span className="tabular-nums">{fmtNum(item.maxSessions)}</span>
            </span>
          </div>
        </div>

        {/* Features */}
        <div className="hidden w-40 md:flex">
          {features.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {visibleFeatures.map((f, i) => (
                <span
                  key={i}
                  className="truncate rounded-full bg-[var(--dash-surface-subtle)] px-2 py-0.5 text-[9px] font-medium text-[var(--dash-text-muted)] ring-1 ring-[var(--dash-border)]"
                  style={{ maxWidth: 80 }}
                >
                  {f}
                </span>
              ))}
              {remaining > 0 && (
                <span className="rounded-full bg-[var(--dash-surface-subtle)] px-2 py-0.5 text-[9px] font-medium text-[var(--dash-text-faint)] ring-1 ring-[var(--dash-border)]">
                  +{remaining}
                </span>
              )}
            </div>
          ) : (
            <span className="text-[11px] text-[var(--dash-text-faint)]">—</span>
          )}
        </div>

        {/* Actions */}
        <div className="w-8" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu
            align="right"
            trigger={
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-lg text-[var(--dash-text-muted)] opacity-0 transition-all hover:bg-[var(--dash-surface-subtle)] group-hover:opacity-100"
              >
                <MoreHorizontal size={14} />
              </button>
            }
            items={menuItems}
          />
        </div>

        <ChevronRight
          size={13}
          className="shrink-0 text-[var(--dash-text-faint)] opacity-0 transition-opacity group-hover:opacity-100"
        />
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={onDelete}
        title="Remover plano?"
        description={`"${item.name}" será removido permanentemente.`}
        confirmLabel="Remover"
        loading={deleting}
      />
    </>
  );
}

const ROW_WIDTHS = [160, 130, 200, 140, 180, 120];

function Bone({ className, style, delay = 0 }) {
  return (
    <div
      className={cn("animate-pulse rounded-lg", className)}
      style={{ background: "var(--dash-surface-hover)", animationDelay: `${delay}ms`, ...style }}
    />
  );
}

function PlanSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
      <div className="hidden items-center gap-4 border-b border-[var(--dash-border)] px-5 py-3 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--dash-text-faint)] md:flex">
        <div className="flex-1">Nome</div>
        <div className="w-28 text-right">Preço</div>
        <div className="w-52">Limites</div>
        <div className="w-40">Features</div>
        <div className="w-8" />
      </div>
      <div className="divide-y divide-[var(--dash-border)]">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-4">
            <div className="flex min-w-0 flex-1">
              <Bone className="h-4" delay={i * 60} style={{ width: ROW_WIDTHS[i % ROW_WIDTHS.length] }} />
            </div>
            <div className="hidden w-28 md:flex justify-end">
              <Bone className="h-4 w-16" delay={i * 60 + 20} />
            </div>
            <div className="hidden w-52 gap-3 md:flex">
              <Bone className="h-4 w-12" delay={i * 60 + 30} />
              <Bone className="h-4 w-12" delay={i * 60 + 40} />
              <Bone className="h-4 w-12" delay={i * 60 + 50} />
            </div>
            <div className="hidden w-40 gap-1 md:flex">
              <Bone className="h-5 w-14 !rounded-full" delay={i * 60 + 60} />
              <Bone className="h-5 w-14 !rounded-full" delay={i * 60 + 70} />
            </div>
            <div className="w-8" />
          </div>
        ))}
      </div>
    </div>
  );
}
